import { ListItem as TiptapListItem } from '@tiptap/extension-list-item';
import { handleBackspace } from './handleBackspace';
import { mergeAttributes } from '@tiptap/core';
import { optimizeStyle } from './utilityFunctions';
import { removeColor } from '../editorUtilityFunctions/listUtilityFunctions';
import {
  splitListItem as pmSplitListItem,
  sinkListItem as pmSinkListItem,
  liftListItem as pmLiftListItem,
} from 'prosemirror-schema-list';
import { Transaction } from '@tiptap/pm/state';

export const ListItem = TiptapListItem.extend({
  name: 'listItem',
  draggable: false,
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      style: {
        default: 'text-align: left; font-size: 16px; color: #212529',
        parseHTML: (element) => {
          if (element.style && !element.style.getPropertyValue('color'))
            element.style.setProperty('color', '#212529');
          if (!element.style.getPropertyValue('font-size'))
            element.style.setProperty('font-size', '16px');
          return element.getAttribute('style');
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'li' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'li',
      optimizeStyle(mergeAttributes(this.options.HTMLAttributes, { ...HTMLAttributes })),
      0,
    ];
  },


  addCommands() {
    return {
      ...this.parent?.(),


      splitListItem:
        () =>
          ({ state, dispatch }) => {
            const { selection, storedMarks } = state;
            const marks = storedMarks || selection.$from.marks();
            const listItemType = this.type;
            const paragraphType = state.schema.nodes.paragraph;

            const $from = selection.$from;
            const parentParagraph = $from.parent;
            const paraAttrs = parentParagraph ? { ...parentParagraph.attrs } : {};

            let handled = false;

            const interceptDispatch = (tr: Transaction) => {
              tr.ensureMarks(marks);

              const pos = tr.selection.from;
              const $pos = tr.doc.resolve(pos);
              const nodePos = pos - $pos.parentOffset;

              const nodeAtPos = tr.doc.nodeAt(nodePos);
              if (nodeAtPos?.type === paragraphType) {
                tr.setNodeMarkup(nodePos, paragraphType, paraAttrs);
              }

              if (dispatch) {
                dispatch(tr);
              }
              handled = true;
            };

            const result = pmSplitListItem(listItemType)(
              state,
              dispatch ? interceptDispatch : undefined
            );

            if (!result) return false;

            if (dispatch && !handled) {
              const tr = state.tr;
              tr.ensureMarks(marks);
              dispatch(tr);
            }

            return true;
          },

      sinkListItem:
        () =>
          ({ state, dispatch }) => {
            const { selection, storedMarks, schema } = state;
            const marks = storedMarks || selection.$from.marks();
            const listItemType = this.type;

            const $from = selection.$from;
            let parentListAttrs: any;

            for (let d = $from.depth; d > 0; d--) {
              const node = $from.node(d);
              if (
                node.type === schema.nodes.bulletList ||
                node.type === schema.nodes.orderedList
              ) {
                parentListAttrs = node.attrs;
                break;
              }
            }

            const interceptDispatch = (tr: Transaction) => {
              const { selection: newSelection } = tr;
              const $newFrom = newSelection.$from;

              if (parentListAttrs) {
                for (let d = $newFrom.depth; d > 0; d--) {
                  const node = $newFrom.node(d);
                  if (
                    node.type === schema.nodes.bulletList ||
                    node.type === schema.nodes.orderedList
                  ) {
                    tr.setNodeMarkup($newFrom.before(d), null, parentListAttrs);
                    break;
                  }
                }
              }

              tr.ensureMarks(marks);

              if (dispatch) {
                dispatch(tr);
              }
            };

            const result = pmSinkListItem(listItemType)(
              state,
              dispatch ? interceptDispatch : undefined
            );

            if (!result) return false;

            return true;
          },

      liftListItem:
        () =>
          ({ tr, state, dispatch }) => {
            const marks = state.storedMarks || state.selection.$from.marks();
            const listItemType = this.type;

            const result = pmLiftListItem(listItemType)(state, dispatch);
            if (!result) return false;

            tr.ensureMarks(marks);
            return true;
          },
    };
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const { editor } = this;
        const { state } = editor;
        const { selection, storedMarks } = state;
        const $from = selection.$from;
        const marks = storedMarks || $from.marks();

        if ($from.parent.content.size === 0) {
          const lifted = editor.commands.liftListItem('listItem');
          if (lifted && marks) {
            marks.forEach((mark) => {
              editor.commands.setMark(mark.type.name, mark.attrs);
            });
          }
          return lifted;
        }

        const listItemNode = $from.node($from.depth - 1);
        const elementAttributes = listItemNode?.attrs || {};

        const paragraphNode = $from.parent;
        const paragraphAttrs = paragraphNode?.attrs || {};

        const textStyleMark = marks?.find((m) => m.type.name === 'textStyle');

        const finalStyle = textStyleMark?.attrs?.color
          ? { style: `color: ${textStyleMark.attrs.color}; ${removeColor(elementAttributes.style)}` }
          : { style: elementAttributes.style };

        const splittedList = editor.commands.splitListItem('listItem');

        if (!splittedList) return false;

        editor.commands.updateAttributes('listItem', {
          ...finalStyle,
        });

        editor.commands.updateAttributes('paragraph', paragraphAttrs);

        if (marks) {
          marks.forEach((mark) => {
            editor.commands.setMark(mark.type.name, mark.attrs);
          });
        }

        return true;
      },

      Tab: () => this.editor.commands.sinkListItem('listItem'),
      'Shift-Tab': () => this.editor.commands.liftListItem('listItem'),
      Backspace: () => {
        return handleBackspace(this.editor, this.name, ['bulletList', 'orderedList', 'listItem']);
      },
      'Shift-Enter': () => this.editor.commands.setHardBreaks('shift'),
    };
  },
});
